import * as React from 'react';
import { createFileRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { MediaTypeEnum, BULK_PHOTO_IMPORT_JOB_TYPE, PERMISSIONS } from '@ooh/contracts';
import { PageTitleContext } from '../__root';
import { ImportWizard } from '@ooh/screens';

// ─── Search params ────────────────────────────────────────────────────────────

const ImportSearchSchema = z.object({
  importJobId: z.coerce.number().int().positive().optional().catch(undefined),
  phase: z
    .enum(['upload', 'supplier-dedup', 'mapping', 'validation', 'progress', 'result'])
    .optional()
    .catch(undefined),
  /** Per-file media type override; pins the adapter for all rows. Cleared on phase change away from mapping. */
  mediaTypeOverride: MediaTypeEnum.optional().catch(undefined),
  /**
   * Job type hint carried from the upload step so the progress phase + stepper render the
   * correct (photo vs XLSX) layout on the first frame, before the status poll returns.
   * Only the photo-bundle type is persisted; XLSX uses the default layout.
   */
  jobType: z.literal(BULK_PHOTO_IMPORT_JOB_TYPE).optional().catch(undefined),
});

export type ImportSearch = z.infer<typeof ImportSearchSchema>;

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/media/import')({
  validateSearch: ImportSearchSchema,
  beforeLoad: ({ context }) => {
    const perms = context.auth.permissions;
    if (perms !== null && !perms.includes(PERMISSIONS.IMPORT)) {
      throw redirect({ to: '/' });
    }
  },
  component: ImportRoute,
});

function ImportRoute() {
  const { setPageTitle } = React.useContext(PageTitleContext);
  const { t } = useTranslation('mediaImport');
  const navigate = useNavigate();
  const search = useSearch({ from: '/media/import' });

  React.useEffect(() => {
    setPageTitle(t('pageTitle'));
  }, [setPageTitle, t]);

  const importJobId = search.importJobId;
  const phase = search.phase ?? (importJobId ? 'validation' : 'upload');
  // Note: 'mapping' phase is set directly from upload response when requiresMapping=true

  function handlePhaseChange(newPhase: string, newJobId?: number, newJobType?: string) {
    // When navigating back to 'upload', clear importJobId so the wizard resets.
    const nextJobId = newPhase === 'upload' ? undefined : (newJobId ?? importJobId);
    // Carry the photo-bundle hint forward (set on upload) and clear it on a fresh upload.
    const nextJobType =
      newPhase === 'upload'
        ? undefined
        : ((newJobType ?? search.jobType) as ImportSearch['jobType']);
    void navigate({
      to: '/media/import',
      search: {
        phase: newPhase as ImportSearch['phase'],
        importJobId: nextJobId,
        // Clear the override when navigating away from the mapping phase
        mediaTypeOverride: newPhase === 'mapping' ? search.mediaTypeOverride : undefined,
        jobType: nextJobType,
      },
    });
  }

  function handleMediaTypeOverrideChange(override: string | null) {
    void navigate({
      to: '/media/import',
      search: {
        ...search,
        mediaTypeOverride: (override ?? undefined) as ImportSearch['mediaTypeOverride'],
      },
      replace: true,
    });
  }

  return (
    <ImportWizard
      importJobId={importJobId}
      currentPhase={
        phase as 'upload' | 'supplier-dedup' | 'mapping' | 'validation' | 'progress' | 'result'
      }
      onPhaseChange={handlePhaseChange}
      mediaTypeOverride={search.mediaTypeOverride}
      onMediaTypeOverrideChange={handleMediaTypeOverrideChange}
      initialJobType={search.jobType}
    />
  );
}
